import { useState, useEffect, useCallback, useRef } from "react";
import { getStudentData, getGroupsData } from '../assets/data.js';
import { getScheduleByDateRange } from '../utils/scheduleService';
import { getStudents as getStudentsFromSupabase } from '../utils/studentsService';
import { getGroups as getGroupsFromSupabase } from '../utils/groupsService';

const SCHEDULE_CACHE_TTL_MS = 5 * 60 * 1000; // 5 минут
let scheduleCache = null;

/** Диапазон дат для текущего вида (YYYY-MM-DD). Загружаем только его, а не всё расписание. */
const getDateRangeForView = (date, mode) => {
  const y = date.getFullYear();
  const m = date.getMonth();
  const d = date.getDate();
  const pad = (n) => String(n).padStart(2, '0');
  if (mode === 'day') {
    const s = `${y}-${pad(m + 1)}-${pad(d)}`;
    return { startDate: s, endDate: s };
  }
  if (mode === 'week') {
    const dayOfWeek = date.getDay();
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const monday = new Date(date);
    monday.setDate(date.getDate() + mondayOffset);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return {
      startDate: `${monday.getFullYear()}-${pad(monday.getMonth() + 1)}-${pad(monday.getDate())}`,
      endDate: `${sunday.getFullYear()}-${pad(sunday.getMonth() + 1)}-${pad(sunday.getDate())}`
    };
  }
  // month: текущий месяц ± 1 месяц
  const start = new Date(y, m - 1, 1);
  const end = new Date(y, m + 2, 0);
  return {
    startDate: `${start.getFullYear()}-${pad(start.getMonth() + 1)}-${pad(start.getDate())}`,
    endDate: `${end.getFullYear()}-${pad(end.getMonth() + 1)}-${pad(end.getDate())}`
  };
};

/** Привести lesson.date к YYYY-MM-DD для сравнения с диапазоном */
const lessonDateToYYYYMMDD = (lesson) => {
  if (!lesson?.date) return null;
  const str = typeof lesson.date === 'string' ? lesson.date : lesson.date.toDateString?.() ?? String(lesson.date);
  if (/^\d{4}-\d{2}-\d{2}/.test(str)) return str.slice(0, 10);
  const parsed = new Date(str);
  if (isNaN(parsed.getTime())) return null;
  const y = parsed.getFullYear();
  const mo = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${y}-${mo}-${day}`;
};

/** Фильтр валидных уроков по дате */
const filterValidLessons = (scheduleData) => {
  if (!scheduleData?.length) return [];
  return scheduleData.filter(lesson => {
    try {
      if (!lesson.date) return false;
      let lessonDate;
      if (typeof lesson.date === 'string') {
        lessonDate = lesson.date.includes('T') ? new Date(lesson.date) : new Date(lesson.date);
      } else {
        lessonDate = lesson.date;
      }
      return !isNaN(lessonDate.getTime());
    } catch {
      return false;
    }
  });
};

export function useScheduleData() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [viewMode, setViewMode] = useState('month'); // 'month', 'week', 'day'
  const [lessons, setLessons] = useState([]);
  const [isLoading, setIsLoading] = useState(false); // Flag to prevent infinite loops

  // State для студентов и групп (загружаются из Supabase)
  const [students, setStudents] = useState({});
  const [groups, setGroups] = useState({});
  const [isLoadingStudentsGroups, setIsLoadingStudentsGroups] = useState(true);

  /** Подгрузить расписание за диапазон и смержить в state (уроки вне диапазона сохраняются) */
  const loadLessonsForRange = useCallback(async (startDate, endDate, silent = false) => {
    try {
      if (!silent) setIsLoading(true);
      const scheduleData = await getScheduleByDateRange(startDate, endDate);
      const validNew = filterValidLessons(scheduleData);
      setLessons(prev => {
        const outOfRange = prev.filter(l => {
          const d = lessonDateToYYYYMMDD(l);
          if (!d) return true;
          return d < startDate || d > endDate;
        });
        const merged = [...outOfRange, ...validNew].sort((a, b) => {
          const da = lessonDateToYYYYMMDD(a) || '';
          const db = lessonDateToYYYYMMDD(b) || '';
          if (da !== db) return da.localeCompare(db);
          const ta = (a.from || a.timeFrom) || '00:00';
          const tb = (b.from || b.timeFrom) || '00:00';
          return ta.localeCompare(tb);
        });
        return merged;
      });
    } catch (error) {
      console.error('❌ Error loading schedule range:', error);
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, []);

  // Load students, groups, and schedule BY RANGE on mount (не всё расписание)
  // При повторном заходе с другой вкладки показываем кэш сразу и при необходимости обновляем в фоне
  const rangeRef = useRef(null);
  useEffect(() => {
    const range = getDateRangeForView(currentDate, viewMode);
    const rangeKey = `${range.startDate}-${range.endDate}`;

    if (scheduleCache && (Date.now() - scheduleCache.timestamp < SCHEDULE_CACHE_TTL_MS)) {
      setStudents(scheduleCache.students || {});
      setGroups(scheduleCache.groups || {});
      setLessons(scheduleCache.lessons || []);
      rangeRef.current = rangeKey;
      setIsLoading(false);
      setIsLoadingStudentsGroups(false);
      loadLessonsForRange(range.startDate, range.endDate, true);
      return;
    }

    const loadAllData = async () => {
      try {
        setIsLoading(true);
        setIsLoadingStudentsGroups(true);
        rangeRef.current = rangeKey;
        console.log('🚀 Schedule: loading students, groups, and schedule range', range.startDate, '..', range.endDate);
        const [studentsData, groupsData, scheduleData] = await Promise.all([
          getStudentsFromSupabase(),
          getGroupsFromSupabase(),
          getScheduleByDateRange(range.startDate, range.endDate)
        ]);
        setStudents(studentsData || {});
        setGroups(groupsData || {});
        setIsLoadingStudentsGroups(false);
        const validLessons = filterValidLessons(scheduleData);
        setLessons(validLessons);
        scheduleCache = { lessons: validLessons, students: studentsData || {}, groups: groupsData || {}, timestamp: Date.now() };
        console.log('📦 Loaded:', { students: Object.keys(studentsData || {}).length, groups: Object.keys(groupsData || {}).length, lessons: validLessons.length });
      } catch (error) {
        console.error('❌ Error loading data:', error);
        setStudents(getStudentData() || {});
        setGroups(getGroupsData() || {});
        setLessons([]);
      } finally {
        setIsLoading(false);
        setIsLoadingStudentsGroups(false);
      }
    };
    loadAllData();
    // Only run on mount; range/view changes are handled by the effect below
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Обновляем кэш при изменении lessons/students/groups, чтобы при следующем заходе были актуальные данные
  useEffect(() => {
    if (scheduleCache && (lessons.length > 0 || Object.keys(students).length > 0)) {
      scheduleCache.lessons = lessons;
      scheduleCache.students = students;
      scheduleCache.groups = groups;
      scheduleCache.timestamp = Date.now();
    }
  }, [lessons, students, groups]);

  // При смене месяца/недели/дня — подгружаем только новый диапазон и мержим
  useEffect(() => {
    const range = getDateRangeForView(currentDate, viewMode);
    const key = `${range.startDate}-${range.endDate}`;
    if (rangeRef.current === key) return;
    rangeRef.current = key;
    loadLessonsForRange(range.startDate, range.endDate, true);
  }, [currentDate, viewMode, loadLessonsForRange]);

  // Обновлять при возврате на вкладку (без лишних запросов по таймеру)
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      const range = getDateRangeForView(currentDate, viewMode);
      loadLessonsForRange(range.startDate, range.endDate, true);
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [currentDate, viewMode, loadLessonsForRange]);

  return {
    currentDate, setCurrentDate,
    selectedDate, setSelectedDate,
    viewMode, setViewMode,
    lessons, setLessons,
    isLoading,
    students, groups, isLoadingStudentsGroups,
  };
}
